import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import PLATES from "@/data/plates.json";

export default function PaperPlatesCollection() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-12">
        <Link
          to="/products"
          data-testid="back-to-products"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#5C665C] hover:text-[#1C2C22]"
        >
          <ArrowLeft size={14} /> All products
        </Link>

        <div className="mt-8 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <p className="overline">Foodservice · Paper Plates</p>
            <h1 className="mt-6 font-serif text-6xl md:text-8xl leading-[0.9] tracking-tighter">
              Paper Plate <span className="italic text-[#C27A62]">collection.</span>
            </h1>
          </div>
          <div className="md:col-span-5">
            <p className="text-lg text-[#5C665C] leading-relaxed">
              {PLATES.length} designs across printed, gold-foil and seasonal
              finishes. Available in dessert (7.5&quot;) and dinner (9.5&quot;) sizes.
            </p>
            <ul
              data-testid="plates-specs"
              className="mt-8 grid grid-cols-2 gap-4 max-w-md"
            >
              {[
                { k: "Sizes", v: "7.5\" · 9.5\"" },
                { k: "Material", v: "Food-safe pulp" },
                { k: "Finish", v: "Printed · Gold foil" },
                { k: "MOQ", v: "On request" },
              ].map((s) => (
                <li key={s.k} className="border-t border-[#D1C9B8] pt-3">
                  <p className="overline">{s.k}</p>
                  <p className="font-serif text-xl mt-1 text-[#1C2C22]">{s.v}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section
        data-testid="plates-grid"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-16"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
          {PLATES.map((p) => (
            <button
              key={p.code}
              data-testid={`plate-${p.code}`}
              onClick={() => setLightbox(p)}
              className="group text-left"
            >
              <div className="aspect-square bg-[#EBE5D9] border border-[#D1C9B8] overflow-hidden relative">
                <img
                  src={p.image}
                  alt={`${p.theme} paper plate`}
                  loading="lazy"
                  className="w-full h-full object-contain p-4 md:p-6 transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] bg-[#F4F1EB] px-2 py-1 border border-[#D1C9B8]">
                  {p.theme}
                </span>
              </div>
              <div className="pt-4 flex items-baseline justify-between gap-3">
                <span className="font-serif text-xl tracking-tight text-[#1C2C22]">
                  {p.code}
                </span>
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#5C665C]">
                  Food-safe
                </span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* CTA BAND */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="bg-[#1C2C22] text-[#F4F1EB] p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <p className="overline text-[#8A9A86]">Bulk pricing & custom prints</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 tracking-tight">
              Order paper plates for your season.
            </h2>
          </div>
          <Link
            to="/contact"
            data-testid="plates-cta-contact"
            className="inline-flex items-center gap-3 border border-[#F4F1EB]/60 px-7 py-4 text-xs uppercase tracking-[0.25em] btn-lift hover:bg-[#F4F1EB] hover:text-[#1C2C22]"
          >
            Request pricing & MOQ →
          </Link>
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          data-testid="plate-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[60] bg-[#1C2C22]/85 backdrop-blur-md flex items-center justify-center p-6 cursor-zoom-out"
        >
          <div
            className="max-w-2xl w-full bg-[#F4F1EB] border border-[#D1C9B8]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-square bg-[#EBE5D9]">
              <img
                src={lightbox.image}
                alt={lightbox.code}
                className="w-full h-full object-contain p-6 md:p-10"
              />
            </div>
            <div className="p-6 md:p-8 flex items-center justify-between gap-6">
              <div>
                <p className="overline">{lightbox.theme}</p>
                <p className="font-serif text-3xl mt-2 tracking-tight">
                  {lightbox.code}
                </p>
                <p className="text-sm text-[#5C665C] mt-1">
                  Dessert 7.5&quot; · Dinner 9.5&quot; · Food-safe
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/contact"
                  data-testid="plate-lightbox-cta"
                  className="bg-[#1C2C22] text-[#F4F1EB] px-5 py-3 text-xs uppercase tracking-[0.25em] btn-lift hover:bg-[#C27A62]"
                >
                  Enquire →
                </Link>
                <button
                  data-testid="plate-lightbox-close"
                  onClick={() => setLightbox(null)}
                  className="text-xs uppercase tracking-[0.25em] text-[#5C665C] hover:text-[#1C2C22]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
